// imports
import React from 'react';
import { Box, Tooltip, useMediaQuery } from '@mui/material';
import { useShortVisualViewport } from '../../../hooks/useShortVisualViewport';
import { styled } from '@mui/material/styles';
import {
  Add,
  Remove,
  MyLocation,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
// local imports
import CustomButton from '../../CustomButton';
import LegendButton from '../LegendButton/LegendButton';
import MapResetButton from '../MapResetButton/MapResetButton';
import MapRulerButton from '../MapRulerButton/MapRulerButton';
import InfoButton from '../InfoButton/InfoButton';
import CollapsedMapTools from './CollapsedMapTools';
import { RootState } from '../../../store';
import { toggleFullExtent } from '../../../store/uiSlice';
import { sharedTooltipProps } from './sharedTooltipProps';
import { MAP_MIN_ZOOM, MAP_MAX_ZOOM } from '../../../constants/mapZoom';


/**
 * CustomMapControls
 *
 * Renders the map overlay buttons on the right side of the map, below the share button.
 * Layout from top to bottom:
 * 1. Legend button (standalone)
 * 2. Zoom in, Zoom out, Full screen (grouped)
 * 3. Ruler button (standalone)
 * 4. Geolocation, Full extent (grouped)
 * 5. Info button (standalone)
 *
 * At small viewport heights (e.g. 200% browser zoom), the buttons collapse into
 * a single "Map tools" button that opens a popover with all controls.
 *
 * @returns {JSX.Element} The rendered custom map controls component.
 */

interface CustomMapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFlyTo: (center: [number, number], zoom: number) => void;
  onResetView: () => void;
  zoom: number;
}

const ControlsContainer = styled(Box)({
  position: 'absolute',
  top: '60px',
  right: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const CustomMapControls = React.memo(
  ({ onZoomIn, onZoomOut, onFlyTo, onResetView, zoom }: CustomMapControlsProps) => {
    const isFullExtent = useSelector(
      (state: RootState) => state.ui.isFullExtent
    );
    const primaryNavOpen = useSelector(
      (state: RootState) => state.map.primaryNavOpen
    );
    const dispatch = useDispatch();
    const isShortViewport = useShortVisualViewport(670);
    const isSmallScreen = useMediaQuery('(max-width: 1023px)');

    const handleGeolocate = () => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            onFlyTo(
              [position.coords.longitude, position.coords.latitude],
              15
            );
          },
          (error) => {
            console.error('Error getting location:', error);
          }
        );
      } else {
        console.error('Geolocation is not supported by this browser.');
      }
    };

    const handleFullExtent = () => {
      dispatch(toggleFullExtent());
    };

    const legendButton = <LegendButton />;
    const rulerButton = <MapRulerButton />;

    const fullExtentButton = (
      <Tooltip
        title={isFullExtent ? 'Exit full extent' : 'Enter full extent'}
        placement="left"
        {...sharedTooltipProps}
      >
        <CustomButton
          onClick={handleFullExtent}
          aria-label={isFullExtent ? 'Exit full extent' : 'Enter full extent'}
          buttonVariant="standalone"
        >
          {isFullExtent ? (
            <FullscreenExit aria-hidden="true" />
          ) : (
            <Fullscreen aria-hidden="true" />
          )}
        </CustomButton>
      </Tooltip>
    );

    const zoomButtons = (
      <ButtonGroup>
        <Tooltip title="Zoom in" placement="left" {...sharedTooltipProps}>
          <CustomButton onClick={onZoomIn} aria-label="Zoom in" disabled={zoom >= MAP_MAX_ZOOM}>
            <Add />
          </CustomButton>
        </Tooltip>
        <Tooltip title="Zoom out" placement="left" {...sharedTooltipProps}>
          <CustomButton onClick={onZoomOut} aria-label="Zoom out" disabled={zoom <= MAP_MIN_ZOOM + 0.01}>
            <Remove />
          </CustomButton>
        </Tooltip>
      </ButtonGroup>
    );

    const navButtons = (
      <ButtonGroup>
        <Tooltip title="Go to current location" placement="left" {...sharedTooltipProps}>
          <CustomButton
            onClick={handleGeolocate}
            aria-label="Go to current location"
          >
            <MyLocation />
          </CustomButton>
        </Tooltip>
        <MapResetButton onResetView={onResetView} />
      </ButtonGroup>
    );

    const hideControls = isSmallScreen && primaryNavOpen;

    if (isShortViewport) {
      return (
        <ControlsContainer sx={{ visibility: hideControls ? 'hidden' : 'visible' }}>
          {legendButton}
          {fullExtentButton}
          <CollapsedMapTools>
            <Box>
              {rulerButton}
              <Box sx={{ mt: '10px' }}>{navButtons}</Box>
            </Box>
            {zoomButtons}
          </CollapsedMapTools>
          <InfoButton />
        </ControlsContainer>
      );
    }

    return (
      <ControlsContainer sx={{ visibility: hideControls ? 'hidden' : 'visible' }}>
        {legendButton}
        {zoomButtons}
        {fullExtentButton}
        {rulerButton}
        {navButtons}
        <InfoButton />
      </ControlsContainer>
    );
  }
);

export default CustomMapControls;
